import styled from "styled-components";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import useIsResMobile from "lib/hooks/useIsResMobile";
import IconWork from "lib/assets/work.svg";
import IconFamily from "lib/assets/family.svg";
import IconStudent from "lib/assets/student.svg";
import IconLawfully from "lib/assets/lawfully.svg";
import IconCitizenship from "lib/assets/immigration.svg";
import { groupBy, prop } from "ramda";
import { useEvent } from "lib/contexts/eventContext";
import { UIEvent } from "lib/enums";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import { isMobile } from "react-device-detect";

dayjs.extend(duration);

interface TimelineProps {
  timeline: ResCaseTimeline | ResCaseTimelineSummary;
}

const Timeline: React.FC<TimelineProps> = ({ timeline }) => {
  const {
    categoryName,
    categoryDescription,
    timelineIconType,
    startDate,
    endDate,
    cases,
  } = timeline;

  /**
   * Assume detail view if events is available
   */
  const events = useMemo(() => {
    if ("events" in timeline) {
      return timeline.events;
    }
    return null;
  }, [timeline]);

  const isDetailView = useMemo(() => (events ? true : false), [events]);

  const { track } = useEvent();

  const handleOnTrackAppDownload = useCallback(() => {
    track(UIEvent.TimelineAppDownload);
  }, [track]);

  return (
    <>
      <WrapperSummary>
        <HeaderSummary
          categoryName={categoryName}
          categoryDescription={categoryDescription}
          timelineIconType={timelineIconType}
          startDate={startDate}
          endDate={endDate}
        />
        <CasesSummary cases={cases} startDate={startDate} endDate={endDate} />
      </WrapperSummary>
      {isDetailView && (
        <WrapperEvents>
          <h5
            css={`
              font-weight: bold;
              font-size: 16px;
              line-height: 24px;
              margin: 0 0 20px;
            `}
          >
            Timeline Map
          </h5>
          {events.map((event) => (
            <RowCaseEvent
              key={event.date}
              event={event}
              startDateAll={startDate}
            />
          ))}
        </WrapperEvents>
      )}
      {isDetailView && (
        <div
          css={`
            font-size: 14px;
            line-height: 22px;
            color: #abafc9;
            margin-top: 20px;
          `}
        >
          This timeline was created with the Lawfully App.{" "}
          <a
            href={getAirbridgeTrackerLink(
              isMobile,
              "Community",
              "Timeline Detail",
            )}
            target="_blank"
            rel="noreferrer"
            css={`
              && {
                color: #abafc9;
                text-decoration: underline;
              }
            `}
            onClick={handleOnTrackAppDownload}
          >
            Download the Lawfully App
          </a>{" "}
          now.
        </div>
      )}
    </>
  );
};

export default Timeline;

const WrapperSummary = styled.div`
  background: #f3f4f8;
  border-radius: 10px;
  padding: 20px;
  overflow: hidden;
`;

const HeaderSummary: React.FC<{
  categoryName: string;
  categoryDescription: string;
  startDate: DateString;
  endDate: DateString;
  timelineIconType: "employment" | "family" | "citizenship" | "student";
}> = ({
  categoryName,
  categoryDescription,
  startDate,
  endDate,
  timelineIconType,
}) => {
  const isResMobile = useIsResMobile();

  const Icon = useMemo(() => {
    switch (timelineIconType) {
      case "family":
        return IconFamily;
      case "student":
        return IconStudent;
      case "employment":
        return IconWork;
      case "citizenship":
        return IconCitizenship;
      default:
        return IconLawfully;
    }
  }, [timelineIconType]);

  const daysTotal = useMemo(
    () => getDaysTotal(startDate, endDate),
    [startDate, endDate],
  );

  return (
    <header
      css={`
        display: flex;
        margin: 0;
        margin-bottom: ${isResMobile ? "10px" : "20px"};
        ${isResMobile
          ? `
            flex-wrap: wrap;
            `
          : ""}
      `}
    >
      {/* first row */}
      <div
        css={`
          flex-shrink: 1;
          flex-wrap: nowrap;
          display: flex;
          flex-grow: 2;
        `}
      >
        {/* icon section */}
        <div
          css={`
            width: 48px;
            height: 48px;
            background-color: white;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            flex-shrink: 0;
          `}
        >
          <Icon />
        </div>
        {/* title section */}
        <h5
          css={`
            margin: 0;
            display: flex;
            margin-left: 18px;
            align-items: flex-start;
            flex-direction: column;
          `}
        >
          <p
            css={`
              font-weight: bold;
              font-size: 18px;
              line-height: 26px;
              margin: 0;
            `}
          >
            {categoryName}
          </p>
          <p
            css={`
              font-weight: normal;
              font-size: 14px;
              line-height: 22px;
              color: #555555;
              margin: 0;
            `}
          >
            {categoryDescription}
          </p>
        </h5>
      </div>
      {/* time elapsed section */}
      <div
        css={`
          font-weight: bold;
          font-size: 14px;
          line-height: 22px;
          flex-shrink: 1;
          flex-basis: 240px;
          text-align: ${isResMobile ? "left" : "right"};
          ${isResMobile
            ? `
            flex-basis: 100%;
            flex-shrink: 0;
            margin-top: 10px;`
            : ""}
        `}
      >{`Total Days: ${daysTotal} days`}</div>
    </header>
  );
};

const CasesSummary: React.FC<{
  cases: Array<ResCaseTimelineCase>;
  startDate: DateString;
  endDate: DateString | null;
}> = ({ cases, startDate, endDate }) => {
  const casesByServiceCenter = groupBy(prop("serviceCenter"), cases);

  const stringServiceCenter = useMemo(
    () =>
      Object.keys(casesByServiceCenter)
        .map(
          (key) =>
            `${key}: ${casesByServiceCenter[key]
              .map((singleCase) => singleCase.caseType)
              .join(", ")}`,
        )
        .join("\n"),
    [casesByServiceCenter],
  );

  return (
    <div>
      {/* heading */}
      <div
        css={`
          margin-top: 10px;
          position: relative;
          display: flex;
          font-weight: normal;
          font-size: 12px;
          line-height: 18px;
          color: #73789d;
          padding: 0 0 6px;
          border-bottom: 1px solid #dcdeea;
          display: flex;
        `}
      >
        <div
          css={`
            flex-basis: 50px;
          `}
        >
          Case
        </div>
        <div
          css={`
            flex-grow: 2;
          `}
        >
          {startDate}
        </div>
        <div
          css={`
            color: ${endDate ? "inherit" : "#195DEE"};
          `}
        >
          {endDate ?? "Today"}
        </div>
      </div>
      {/* cases */}
      {cases.map((singleCase) => (
        <RowCaseSummary
          key={singleCase.caseId}
          dataCase={singleCase}
          startDateAll={startDate}
          endDateAll={endDate}
        />
      ))}
      {/* service center */}
      <div
        css={`
          margin-top: 20px;
          font-size: 12px;
          line-height: 18px;
          color: #73789d;
          white-space: pre-line;
        `}
      >
        {stringServiceCenter}
      </div>
    </div>
  );
};

const RowCaseSummary: React.FC<{
  dataCase: ResCaseTimelineCase;
  startDateAll: DateString;
  endDateAll: DateString | null;
}> = ({ dataCase, startDateAll, endDateAll }) => {
  const isOngoing = useMemo(
    () => (dataCase.endDate ? false : true),
    [dataCase.endDate],
  );

  /**
   * Represents the amount of days past for all cases (timeline)
   */
  const daysTotalAll = useMemo(
    () => getDaysTotal(startDateAll, endDateAll),
    [startDateAll, endDateAll],
  );

  /**
   * Represents the amount of days past for this particular case
   */
  const daysTotalCase = useMemo(
    () => getDaysTotal(dataCase.startDate, dataCase.endDate),
    [dataCase.startDate, dataCase.endDate],
  );

  /**
   * Represents the offset percentage (in terms of amount of total days past as 100%)
   * We calculate the percentage offset by comparing the start dates of this particular case
   * and the timeline
   */
  const percentageOffset = useMemo(() => {
    const offset = getDaysOffset(startDateAll, dataCase.startDate);
    return ((offset / daysTotalAll) * 100).toFixed(2);
  }, [dataCase.startDate, startDateAll, daysTotalAll]);

  return (
    <div
      css={`
        display: flex;
        margin-top: 10px;
        width: 100%;
        position: relative;
      `}
    >
      <div
        css={`
          min-width: 50px;
          font-weight: bold;
          font-size: 14px;
          line-height: 22px;
          color: ${isOngoing ? "#195DEE" : "#00194d"};
        `}
      >
        {dataCase.caseType}
      </div>
      <div
        css={`
          /* take up all available space */
          flex-grow: 1;
        `}
      >
        <div
          css={`
            background-color: ${isOngoing ? "#195DEE" : "#DCDEEA"};
            height: 20px;
            border-radius: 4px;
            width: ${((daysTotalCase / daysTotalAll) * 100).toFixed(2)}%;
            margin-left: ${percentageOffset}%;
            color: ${isOngoing ? "white" : "#777777"};
            font-weight: normal;
            font-size: 12px;
            line-height: 18px;
            position: relative;
            flex-shrink: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            white-space: nowrap;
          `}
        >
          <div>{`${daysTotalCase} Days`}</div>
        </div>
      </div>
    </div>
  );
};

const WrapperEvents = styled.div`
  margin-top: 30px;
`;

/**
 * Represents events grouped by a single date
 */
const RowCaseEvent: React.FC<{
  event: ResCaseTimelineEvent;
  startDateAll: DateString;
}> = ({ event, startDateAll }) => {
  const eventsDefault = useMemo(() => {
    if (event.defaultEvents.length > 0) {
      return event.defaultEvents;
    }
    return null;
  }, [event.defaultEvents]);

  const eventsCustom = useMemo(() => {
    if (event.customEvents.length > 0) {
      return event.customEvents;
    }
    return null;
  }, [event.customEvents]);

  const daysElapsedFromStart = useMemo(() => {
    return getDaysOffset(startDateAll, event.date);
  }, [event.date, startDateAll]);

  return (
    <>
      {eventsDefault?.map(({ eventName, caseTypes }) => (
        <RowCaseEventDetail
          key={eventName}
          date={event.date}
          caseTypes={caseTypes}
          eventName={eventName}
          daysElapsed={daysElapsedFromStart}
        />
      ))}
      {eventsCustom?.map(({ eventName, caseTypes }) => (
        <RowCaseEventDetail
          key={eventName}
          isCustom
          date={event.date}
          caseTypes={caseTypes}
          eventName={eventName}
          daysElapsed={daysElapsedFromStart}
        />
      ))}
    </>
  );
};

/**
 * Represents a single unit of event on the case timeline
 * A single RowCaseEvent can render multiple of these
 */
const RowCaseEventDetail: React.FC<{
  date: DateString;
  caseTypes: string[];
  eventName: string;
  isCustom?: boolean;
  daysElapsed: number;
}> = ({ date, caseTypes, eventName, isCustom, daysElapsed }) => {
  const isResMobile = useIsResMobile();

  return (
    <div
      css={`
        position: relative;
        display: flex;
        flex-direction: column;
        padding-bottom: 30px;
      `}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width="100%"
        height="100%"
        css={`
          position: absolute;
          z-index: 0;
          overflow: visible;
        `}
      >
        <line
          x1="4px"
          x2="4px"
          y1="0"
          y2="100%"
          css={`
            stroke: rgba(221, 232, 255, 0.5);
            stroke-width: 8px;
            stroke-linecap: round;
          `}
        />
        <circle
          cx="4"
          cy="10"
          r="4"
          css={`
            fill: #195dee;
          `}
        />
      </svg>
      {/* content */}
      <div
        css={`
          margin-left: 20px;
          display: flex;
          flex-direction: column;
          position: relative;
        `}
      >
        <div
          css={`
            display: flex;
            flex-wrap: wrap;
          `}
        >
          {/* top row */}
          <div
            css={`
              font-weight: 500;
              font-size: 14px;
              line-height: 22px;
              color: #195dee;
              margin-right: 18px;
              order: 0;
            `}
          >
            {date}
          </div>
          <div
            css={`
              order: ${isResMobile ? 2 : 1}};
              flex-grow: 2;
              flex-basis: ${isResMobile ? "100%" : "auto"};
              display: flex;
              margin-top: ${isResMobile ? "10px" : 0};
            `}
          >
            {caseTypes?.map((type) => (
              <LabelCaseType key={type}>{type}</LabelCaseType>
            ))}
          </div>
          <div
            css={`
              display: flex;
              flex-grow: 1;
              justify-content: flex-end;
              order: ${isResMobile ? 1 : 2};
            `}
          >
            {/* custom event */}
            {isCustom && (
              <div>
                <LabelEventCustom>Added</LabelEventCustom>
              </div>
            )}
            {/* days elapsed */}
            {daysElapsed !== 0 ? (
              <div
                css={`
                  font-size: 14px;
                  line-height: 22px;
                  text-align: right;
                  color: #73789d;
                `}
              >{`D${daysElapsed > 0 ? `+` : ""}${daysElapsed}`}</div>
            ) : null}
          </div>
        </div>
        {/* bottom row */}
        <div
          css={`
            font-weight: bold;
            font-size: 16px;
            line-height: 24px;
            color: black;
            margin-top: 16px;
          `}
        >
          {eventName}
        </div>
      </div>
    </div>
  );
};

const LabelCaseType = styled.div`
  background: #f3f4f8;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  line-height: 22px;
  color: black;
  padding: 0 6px;
  margin-right: 10px;
`;

const LabelEventCustom = styled(LabelCaseType)`
  background: #e3ecff;
  color: #195dee;
`;

function getDaysTotal(
  startDate: DateString,
  endDate: DateString | null,
): number {
  if (!endDate) {
    return Math.ceil(
      dayjs
        .duration(dayjs().startOf("day").valueOf() - dayjs(startDate).valueOf())
        .asDays(),
    );
  }
  return Math.ceil(
    dayjs
      .duration(dayjs(endDate).valueOf() - dayjs(startDate).valueOf())
      .asDays(),
  );
}

function getDaysOffset(fromDate: DateString, toDate: DateString): number {
  return Number(
    dayjs
      .duration(dayjs(toDate).valueOf() - dayjs(fromDate).valueOf())
      .asDays()
      .toFixed(0),
  );
}
